import React, { useEffect, useState } from 'react'

interface GoogleMapsLoaderProps {
    children: React.ReactNode
    onLoad?: () => void
    onError?: (error: Error) => void
}

/**
 * GoogleMapsLoader component - Handles loading the Google Maps JavaScript API
 *
 * This component ensures the Google Maps API is loaded before rendering its children
 * It prevents multiple script loads and handles loading states
 */
const GoogleMapsLoader: React.FC<GoogleMapsLoaderProps> = ({ children, onLoad, onError }) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [hasError, setHasError] = useState(false)

    useEffect(() => {
        // Get API key from environment variables - try multiple possible env variable names
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY

        if (!apiKey) {
            console.warn('Google Maps API key not found in environment variables (REACT_APP_GOOGLE_MAPS_API_KEY)')
            setHasError(true)
            onError?.(new Error('Google Maps API key not found in environment variables'))
            return
        }

        // Check if Google Maps is already loaded
        if (window.google && window.google.maps) {
            setIsLoaded(true)
            onLoad?.()
            return
        }

        // Check if the script is already being loaded
        const existingScript = document.getElementById('google-maps-script')
        if (existingScript) {
            // Wait for the existing script to load
            const handleExistingScriptLoad = () => {
                setIsLoaded(true)
                onLoad?.()
            }

            existingScript.addEventListener('load', handleExistingScriptLoad)
            existingScript.addEventListener('error', () => {
                setHasError(true)
                onError?.(new Error('Failed to load Google Maps API'))
            })

            return () => {
                existingScript.removeEventListener('load', handleExistingScriptLoad)
            }
        }

        // Load the script
        const script = document.createElement('script')
        script.id = 'google-maps-script'
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`
        script.async = true
        script.defer = true

        script.addEventListener('load', () => {
            setIsLoaded(true)
            onLoad?.()
        })

        script.addEventListener('error', () => {
            setHasError(true)
            onError?.(new Error('Failed to load Google Maps API'))
        })

        document.head.appendChild(script)

        return () => {
            // Clean up event listeners but don't remove the script
            script.removeEventListener('load', () => {
                setIsLoaded(true)
                onLoad?.()
            })

            script.removeEventListener('error', () => {
                setHasError(true)
                onError?.(new Error('Failed to load Google Maps API'))
            })
        }
    }, [onLoad, onError])

    if (hasError) {
        return (
            <div
                style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#f44336',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <h3>Failed to load Google Maps</h3>
                <p>Please check your API key in environment variables (REACT_APP_GOOGLE_MAPS_API_KEY).</p>
            </div>
        )
    }

    if (!isLoaded) {
        return (
            <div
                style={{
                    padding: '20px',
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div>Loading Google Maps...</div>
            </div>
        )
    }

    return <>{children}</>
}

export default GoogleMapsLoader
